import { SIGNED_URL_EXPIRATION_MS, type BucketFile } from "@/hooks/useSupabaseStorage";

export const getFromLocalStorage = (key: string) => window.localStorage.getItem(key);
export const setToLocalStorage = (key: string, value: string) => window.localStorage.setItem(key, value);
export const removeFromLocalStorage = (key: string) => window.localStorage.removeItem(key);
const getFutureDateISO = (ms: number): string => new Date(Date.now() + ms).toISOString();

export const clearLocalStorage = () => {
  [window.localStorage, window.sessionStorage].forEach((storage) => {
    Object.entries(storage).forEach(([key]) => storage.removeItem(key));
  });
};

interface SetCachedSignedFileUrlProps {
  file: BucketFile;
  identifier?: string;
  url?: string;
  expiration?: number;
}
export const setCachedSignedFileUrl = ({
  file,
  identifier = "",
  url = "",
  expiration = SIGNED_URL_EXPIRATION_MS,
}: SetCachedSignedFileUrlProps) => {
  setToLocalStorage(`STORAGE::ID${identifier}::${file.id}`, file.id);
  setToLocalStorage(`STORAGE::BUCKET${identifier}::${file.bucket_id}`, file.bucket_id);
  setToLocalStorage(`STORAGE::PATH${identifier}::${file.path}`, file.path);
  setToLocalStorage(`STORAGE::URL${identifier}::${file.id}`, url);
  setToLocalStorage(`STORAGE::EXP${identifier}::${file.id}`, getFutureDateISO(expiration));
};

interface GetCachedSignedFileUrlProps {
  file: BucketFile;
  identifier?: string;
}
export const getCachedSignedFileUrl = ({ file, identifier = "" }: GetCachedSignedFileUrlProps) => {
  const id = getFromLocalStorage(`STORAGE::ID${identifier}::${file.id}`);
  const bucket = getFromLocalStorage(`STORAGE::BUCKET${identifier}::${file.bucket_id}`);
  const path = getFromLocalStorage(`STORAGE::PATH${identifier}::${file.path}`);
  const url = getFromLocalStorage(`STORAGE::URL${identifier}::${file.id}`);
  const exp = getFromLocalStorage(`STORAGE::EXP${identifier}::${file.id}`);

  const idValid = Boolean(id) && id === file.id;
  const bucketValid = Boolean(bucket) && bucket === file.bucket_id;
  const pathValid = Boolean(path) && path === file.path;
  const urlValid = Boolean(url);
  const expValid = exp ? new Date(exp).getTime() > new Date().getTime() : false;

  const isValid = idValid && bucketValid && pathValid && urlValid && expValid;

  if (isValid) return url;

  removeFromLocalStorage(`STORAGE::ID${identifier}::${file.id}`);
  removeFromLocalStorage(`STORAGE::BUCKET${identifier}::${file.bucket_id}`);
  removeFromLocalStorage(`STORAGE::PATH${identifier}::${file.path}`);
  removeFromLocalStorage(`STORAGE::URL${identifier}::${file.id}`);
  removeFromLocalStorage(`STORAGE::EXP${identifier}::${file.id}`);
};
